import {
  useFeatureAdminActions,
  useIcpAssignablePermissions,
  useSystemRoles
} from '@lib/features/useFeatureAdmin'
import type { SystemRoleDetail, SystemRoleRow } from '@lib/features/types'
import {
  Alert,
  Button,
  Group,
  Modal,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  FeaturePermissionCheckboxGroups,
  groupPermissionsByFeature
} from '@packages/core'
import { H2, SortableTable } from '@packages/ui'
import { IconEdit } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { mutate } from 'swr'

const HUB_ICP_TYPE = 'hub'

export function SystemRolesSection() {
  const { data: roles = [], isLoading, error } = useSystemRoles()
  const { data: hubPermissions = [] } = useIcpAssignablePermissions(HUB_ICP_TYPE)
  const { fetchSystemRole, updateSystemRole } = useFeatureAdminActions()

  const [opened, { open, close }] = useDisclosure(false)
  const [selectedRole, setSelectedRole] = useState<SystemRoleRow | null>(null)
  const [roleDetail, setRoleDetail] = useState<SystemRoleDetail | null>(null)
  const [description, setDescription] = useState('')
  const [permissionIds, setPermissionIds] = useState<string[]>([])
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const permissionGroups = useMemo(
    () =>
      groupPermissionsByFeature(
        hubPermissions.map((p) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          feature_key: p.feature_key,
          feature_name: p.feature_name ?? undefined,
          feature_sort_order: p.feature_sort_order ?? undefined
        }))
      ),
    [hubPermissions]
  )

  const assignableIds = useMemo(
    () => new Set(hubPermissions.map((p) => p.id)),
    [hubPermissions]
  )

  const openRole = async (role: SystemRoleRow) => {
    setSelectedRole(role)
    setSaveError(null)
    setLoadingDetail(true)
    open()
    try {
      const detail = await fetchSystemRole(role.id)
      setRoleDetail(detail)
      setDescription(detail.description || '')
      setPermissionIds(
        detail.permission_ids.filter((id) => assignableIds.has(id))
      )
    } finally {
      setLoadingDetail(false)
    }
  }

  useEffect(() => {
    if (!opened || !roleDetail) return
    setPermissionIds(
      roleDetail.permission_ids.filter((id) => assignableIds.has(id))
    )
  }, [assignableIds, opened, roleDetail])

  const handleClose = () => {
    close()
    setSelectedRole(null)
    setRoleDetail(null)
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!roleDetail) return
    setSaving(true)
    setSaveError(null)
    try {
      const scoped = permissionIds.filter((id) => assignableIds.has(id))
      await updateSystemRole({
        role_id: roleDetail.id,
        description,
        permission_ids: scoped
      })
      await mutate('admin-system-roles')
      handleClose()
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save role')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack gap="md">
      <Stack gap={4}>
        <H2 className="text-white">System role templates (Hub)</H2>
        <Text size="sm" c="dimmed">
          Platform roles with tenant_id NULL. Permission grants must stay within
          the Hub ICP feature ceiling.
        </Text>
      </Stack>

      {error && (
        <Alert color="red" title="Failed to load system roles">
          {error.message}
        </Alert>
      )}

      <SortableTable
        data={roles}
        loading={isLoading}
        defaultSortBy="name"
        defaultSortAsc
        highlightOnHover
        noResultsMessage="No system roles found."
        columns={[
          { key: 'name', label: 'Role' },
          { key: 'description', label: 'Description' },
          { key: 'actor_kind', label: 'Actor' },
          { key: 'user_type', label: 'User type' },
          { key: 'permission_count', label: 'Permissions' },
          {
            key: 'id',
            label: '',
            sortable: false,
            render: (_value: string, role: SystemRoleRow) =>
              role.account_owner ? (
                <Text size="xs" c="dimmed">
                  Owner
                </Text>
              ) : (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconEdit size={14} />}
                  onClick={() => void openRole(role)}
                >
                  Edit
                </Button>
              )
          }
        ]}
      />

      <Modal
        opened={opened}
        onClose={handleClose}
        title={selectedRole ? `System role — ${selectedRole.name}` : 'System role'}
        size="lg"
      >
        {loadingDetail || !roleDetail ? (
          <Text size="sm" c="dimmed">
            Loading…
          </Text>
        ) : roleDetail.account_owner ? (
          <Alert>
            Account owner roles include all permissions within the tenant feature
            ceiling and cannot be edited here.
          </Alert>
        ) : (
          <Stack gap="md">
            <TextInput label="Name" value={roleDetail.name} disabled />
            <TextInput
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Text size="sm" fw={500}>
              Permissions ({permissionIds.length})
            </Text>
            <Text size="xs" c="dimmed">
              Only permissions for modules enabled in the Hub ICP matrix are
              assignable.
            </Text>
            <FeaturePermissionCheckboxGroups
              groups={permissionGroups}
              value={permissionIds}
              onChange={setPermissionIds}
            />
            {saveError && (
              <Alert color="red" title="Save failed">
                {saveError}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose}>
                Cancel
              </Button>
              <Button loading={saving} onClick={() => void handleSave()}>
                Save
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  )
}
